/* eslint-disable react/prop-types */
import { useNavigate } from 'react-router-dom';
import {
  Store,
  User,
  Bell,
  CreditCard,
  Shield,
  HelpCircle,
  ChevronRight,
  Settings as SettingsIcon,
  Tag,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useUser } from '@/contexts/UserContext';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useUser();

  const settingsSections = [
    {
      title: 'Account Settings',
      items: [
        {
          icon: User,
          label: 'Profile Information',
          description: 'Update your personal details',
          path: '/profile/edit',
          color: 'text-blue-600',
          bg: 'bg-blue-50',
        },
        {
          icon: Store,
          label: 'Shop Settings',
          description: 'Manage your shop information',
          path: '/profile/settings',
          color: 'text-orange-600',
          bg: 'bg-orange-50',
        },
      ],
    },
    {
      title: 'Business Settings',
      items: [
        {
          icon: Tag,
          label: 'Coupons',
          description: 'Create and manage discount coupons',
          path: '/coupons',
          color: 'text-pink-600',
          bg: 'bg-pink-50',
        },
        {
          icon: CreditCard,
          label: 'Payment Methods',
          description: 'Manage payment options',
          path: '/profile',
          color: 'text-green-600',
          bg: 'bg-green-50',
        },
        {
          icon: Bell,
          label: 'Notifications',
          description: 'Configure notification preferences',
          path: '/profile',
          color: 'text-purple-600',
          bg: 'bg-purple-50',
        },
      ],
    },
    {
      title: 'Support & Help',
      items: [
        {
          icon: HelpCircle,
          label: 'Help Center',
          description: 'Get answers to common questions',
          path: '/faq',
          color: 'text-indigo-600',
          bg: 'bg-indigo-50',
        },
        {
          icon: Shield,
          label: 'Privacy & Security',
          description: 'Manage your privacy settings',
          path: '/privacy',
          color: 'text-red-600',
          bg: 'bg-red-50',
        },
      ],
    },
  ];

  return (
    <div className="h-full p-4 md:p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your account and shop preferences</p>
          </div>
        </div>

        {/* User Info Card */}
        <Card className="border-gray-200 bg-gradient-to-r from-orange-50 to-orange-100">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md">
                <Store className="w-8 h-8 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{user?.shopName || 'Shop Name'}</h2>
                <p className="text-sm text-gray-600">{user?.name || 'Owner Name'}</p>
                <p className="text-xs text-gray-500 mt-1">{user?.contactNo || 'Phone Number'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings Sections */}
        {settingsSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              {section.title}
            </h2>
            <Card className="border-gray-200">
              <CardContent className="p-0">
                {section.items.map((item, itemIndex) => (
                  <button
                    key={itemIndex}
                    onClick={() => navigate(item.path)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${item.bg}`}>
                        <item.icon className={`w-5 h-5 ${item.color}`} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-semibold text-gray-900">{item.label}</p>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>
        ))}

        {/* App Information */}
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-center justify-center">
              <SettingsIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-600">Biglocal Vendor Dashboard</p>
                <p className="text-xs text-gray-400 mt-1">Version 1.0.0</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
